import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { HiMagnifyingGlass, HiCalendar, HiArrowRight, HiBookOpen, HiArrowPath, HiClock, HiSparkles, HiFire } from 'react-icons/hi2';

const BlogList = ({ db, appId, onSelectArticle }) => {
    const [articles, setArticles] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('Semua');

    useEffect(() => {
        fetchPublishedArticles();
    }, []);

    const fetchPublishedArticles = async () => {
        setIsLoading(true);
        try {
            const articlesCol = collection(db, "artifacts", appId, "public", "data", "articles");
            const snapshot = await getDocs(articlesCol);
            const articleList = snapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                .filter(art => art.status === 'published' || !art.status)
                .sort((a, b) => {
                    const timeA = a.createdAt?.seconds || (a.createdAt?.toDate ? a.createdAt.toDate().getTime() / 1000 : 0);
                    const timeB = b.createdAt?.seconds || (b.createdAt?.toDate ? b.createdAt.toDate().getTime() / 1000 : 0);
                    return timeB - timeA;
                });
            setArticles(articleList);
        } catch (error) {
            console.error("Error fetching blog articles:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const categories = ['Semua', ...new Set(articles.map(a => a.category).filter(Boolean))];

    const filteredArticles = articles.filter(art => {
        const matchesSearch = art.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                              art.seoDescription?.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesCategory = selectedCategory === 'Semua' || art.category === selectedCategory;
        return matchesSearch && matchesCategory;
    });

    const getReadingTime = (content) => {
        if (!content) return '2 min baca';
        const words = content.split(/\s+/).length;
        const mins = Math.max(1, Math.ceil(words / 200));
        return `${mins} min baca`;
    };

    return (
        <div className="min-h-screen bg-white text-gray-900 pb-16">
            {/* STICKY TOP SEARCH & FILTER BAR (AIRBNB STYLE) */}
            <div className="sticky top-0 z-20 bg-white/90 backdrop-blur-md border-b border-gray-100 px-4 py-3 shadow-sm transition-all">
                <div className="max-w-4xl mx-auto space-y-3">
                    {/* AIRBNB SEARCH CAPSULE */}
                    <div className="relative flex items-center">
                        <div className="absolute left-4 text-gray-400">
                            <HiMagnifyingGlass className="w-5 h-5" />
                        </div>
                        <input
                            type="text"
                            placeholder="Cari berita, promo, tips nongkrong..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-11 pr-8 py-2.5 bg-gray-100/80 hover:bg-gray-100 focus:bg-white border border-transparent focus:border-green-700 rounded-full shadow-inner text-sm font-medium text-gray-800 placeholder-gray-400 focus:outline-none transition-all"
                        />
                        {searchQuery && (
                            <button
                                onClick={() => setSearchQuery('')}
                                className="absolute right-3 bg-gray-200 text-gray-600 rounded-full p-1 text-xs hover:bg-gray-300"
                            >
                                ✕
                            </button>
                        )}
                    </div>

                    {/* CATEGORIES PILLS (AIRBNB STYLE HORIZONTAL SCROLL) */}
                    <div className="flex items-center gap-2 overflow-x-auto no-scrollbar py-1">
                        {categories.map((cat) => {
                            const isActive = selectedCategory === cat;
                            return (
                                <button
                                    key={cat}
                                    onClick={() => setSelectedCategory(cat)}
                                    className={`px-4 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all flex items-center gap-1.5 ${
                                        isActive
                                            ? 'bg-green-800 text-white shadow-md scale-105'
                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900'
                                    }`}
                                >
                                    {cat === 'Semua' && <HiSparkles className="w-3.5 h-3.5" />}
                                    {cat === 'Berita & Promo' && <HiFire className="w-3.5 h-3.5 text-amber-400" />}
                                    <span>{cat}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* MAIN CONTENT AREA */}
            <div className="max-w-4xl mx-auto px-4 pt-6">
                {/* PAGE TITLE */}
                <div className="mb-6 flex items-center justify-between">
                    <div>
                        <h1 className="text-xl md:text-2xl font-black text-gray-900 tracking-tight flex items-center gap-2">
                            <span>Blog & Cerita Semesta</span>
                            <span className="text-xs font-bold px-2.5 py-0.5 bg-green-100 text-green-800 rounded-full">
                                {filteredArticles.length} Artikel
                            </span>
                        </h1>
                        <p className="text-xs text-gray-500 mt-0.5">
                            Rekomendasi menu, tips nugas, promo & cerita terbaru
                        </p>
                    </div>
                </div>

                {/* ARTICLES LIST (AIRBNB CARD STYLE) */}
                {isLoading ? (
                    <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                        <HiArrowPath className="animate-spin text-green-700 w-9 h-9 mb-2" />
                        <span className="text-xs font-medium">Memuat artikel...</span>
                    </div>
                ) : filteredArticles.length === 0 ? (
                    <div className="text-center py-16 bg-gray-50 rounded-3xl border border-gray-100">
                        <HiBookOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-600 font-semibold text-sm">Belum ada artikel ditemukan</p>
                        <p className="text-xs text-gray-400 mt-1">Coba gunakan kata kunci pencarian atau kategori lain</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8">
                        {filteredArticles.map((art) => (
                            <article
                                key={art.id}
                                onClick={() => onSelectArticle(art)}
                                className="group cursor-pointer flex flex-col space-y-3"
                            >
                                {/* AIRBNB HERO IMAGE CARD CONTAINER */}
                                <div className="relative aspect-[16/10] w-full rounded-2xl overflow-hidden bg-gray-100 shadow-sm border border-gray-100/80 group-hover:shadow-md transition-all duration-300">
                                    {art.coverImage ? (
                                        <img
                                            src={art.coverImage}
                                            alt={art.title}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ease-out"
                                        />
                                    ) : (
                                        <div className="w-full h-full bg-gradient-to-br from-green-800 via-emerald-900 to-teal-950 flex flex-col items-center justify-center text-white p-4 text-center">
                                            <HiBookOpen className="w-10 h-10 mb-1 opacity-80" />
                                            <span className="font-bold text-sm tracking-wide">Semesta Cafe</span>
                                        </div>
                                    )}

                                    {/* CATEGORY BADGE FLOATING TOP LEFT */}
                                    <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-md text-gray-900 text-[11px] font-bold px-3 py-1 rounded-full shadow-sm border border-white/50">
                                        {art.category || 'Umum'}
                                    </div>

                                    {/* READING TIME BADGE BOTTOM RIGHT */}
                                    <div className="absolute bottom-3 right-3 bg-black/60 backdrop-blur-md text-white text-[10px] font-medium px-2.5 py-0.5 rounded-full flex items-center gap-1">
                                        <HiClock className="w-3 h-3" />
                                        <span>{getReadingTime(art.content)}</span>
                                    </div>
                                </div>

                                {/* TYPOGRAPHY BELOW IMAGE (AIRBNB STYLE) */}
                                <div className="space-y-1.5 px-0.5">
                                    <div className="flex items-center gap-2 text-[11px] text-gray-400 font-medium">
                                        <HiCalendar className="w-3.5 h-3.5 text-gray-400" />
                                        <span>
                                            {art.createdAt?.toDate
                                                ? art.createdAt.toDate().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })
                                                : 'Baru Saja'}
                                        </span>
                                    </div>

                                    <h2 className="text-base md:text-lg font-bold text-gray-900 leading-snug group-hover:text-green-800 transition-colors line-clamp-2">
                                        {art.title}
                                    </h2>

                                    <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed font-normal">
                                        {art.seoDescription || art.content?.replace(/<[^>]*>?/gm, '').substring(0, 120)}
                                    </p>

                                    <div className="pt-1 flex items-center text-xs font-bold text-green-800 group-hover:translate-x-1 transition-transform gap-1">
                                        <span>Baca Selengkapnya</span>
                                        <HiArrowRight className="w-3.5 h-3.5" />
                                    </div>
                                </div>
                            </article>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default BlogList;
